import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import {
  MailOutline as MailOutlineIcon,
  DoneAll as DoneAllIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import EmailDetailView from './EmailDetailView';
import { apiCall } from "../../services/ApiCall";
import config from "../../config/config.json";

// Email Status Chip Component
const EmailStatusChip = ({ status, openedAt }) => {
  // Determine if email is opened based on openedAt timestamp
  const isOpened = openedAt !== null;
  const displayStatus = isOpened ? 'opened' : status;
  
  const statusConfig = {
    sent: { 
      label: 'Sent', 
      color: '#ff9800', 
      icon: <SendIcon sx={{ fontSize: 14 }} /> 
    },
    opened: { 
      label: 'Opened', 
      color: '#4caf50', 
      icon: <DoneAllIcon sx={{ fontSize: 14 }} /> 
    },
    delivered: { 
      label: 'Delivered', 
      color: '#2196f3', 
      icon: <DoneAllIcon sx={{ fontSize: 14 }} /> 
    },
    bounced: { 
      label: 'Bounced', 
      color: '#f44336', 
      icon: <SendIcon sx={{ fontSize: 14 }} /> 
    },
    failed: { 
      label: 'Failed', 
      color: '#f44336', 
      icon: <SendIcon sx={{ fontSize: 14 }} /> 
    }
  };

  const config = statusConfig[displayStatus] || statusConfig.sent;

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        bgcolor: 'transparent',
        color: config.color,
        border: `1px solid ${config.color}`,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: '24px',
        '& .MuiChip-icon': {
          color: config.color
        }
      }}
    />
  );
};

// Table View for Large Screens
const EmailTableView = ({ emails, formatDateTime, onEmailClick }) => (
  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Subject</TableCell>
          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Date</TableCell>
          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Sent By</TableCell>
          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {emails.map((email) => (
          <TableRow 
            key={email.id}
            onClick={() => onEmailClick(email)}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#f9fafb'
              },
              '& td': {
                borderBottom: '1px solid #e0e0e0'
              }
            }}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'transparent', border: '1px solid #e0e0e0', width: 32, height: 32 }}>
                  <MailOutlineIcon sx={{ fontSize: 18, color: '#666' }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                    {email.subject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {email.recipient}
                  </Typography>
                </Box>
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {formatDateTime(email.sentDate)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {email.sentBy || 'System'}
              </Typography>
            </TableCell>
            <TableCell>
              <EmailStatusChip status={email.status} openedAt={email.openedAt} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Card View for Mobile Screens
const EmailCardView = ({ emails, formatDateTime, onEmailClick }) => (
  <Box>
    {emails.map((email) => (
      <Paper 
        key={email.id}
        onClick={() => onEmailClick(email)}
        sx={{ 
          mb: 2, 
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderColor: '#1976d2'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Avatar sx={{ bgcolor: 'transparent', border: '1px solid #e0e0e0', width: 36, height: 36 }}>
              <MailOutlineIcon sx={{ fontSize: 18, color: '#666' }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {email.subject}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {email.recipient}
              </Typography>
            </Box>
          </Box>
          <EmailStatusChip status={email.status} openedAt={email.openedAt} />
        </Box>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              {formatDateTime(email.sentDate)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Sent By
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              {email.sentBy || 'System'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    ))}
  </Box>
);

// Empty State Component
const EmptyEmailState = () => (
  <Box sx={{ 
    textAlign: 'center', 
    py: 6,
    color: 'text.secondary'
  }}>
    <MailOutlineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
    <Typography variant="body1">
      No emails found for this request
    </Typography>
  </Box>
);

// Main Emails Tab Component
const EmailsTab = ({ requestId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(20);


  const visibleEmails = emails.slice(0, visibleCount);
  const containerRef = useRef(null);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Fetch emails on component mount
  useEffect(() => {
    if (requestId) {
      fetchEmails();
    }
  }, [requestId]);

  // Fetch emails from API
const fetchEmails = async () => {
  setLoading(true);
  setError(null);
  
  try {
    const response = await apiCall(
      `${config.baseURL}communication/emails/request/${requestId}`,
      {},
      {},
      "GET"
    );
    
    console.log('API Response:', response); // Debug log
    
    const { isSuccess, data } = response;
    
    if (isSuccess && data) {
      console.log('Raw data:', data); // Debug log
      console.log('Is array?', Array.isArray(data)); // Debug log
      
      const transformedEmails = Array.isArray(data) 
        ? data.map(email => ({
            id: email.id,
            requestId: email.requestId,
            subject: email.subject,
            recipient: email.to,
            cc: email.cc || '',
            bcc: email.bcc || '',
            from: email.from,
            sentDate: email.sentAt || email.createdAt,
            sentBy: email.sentBy || (email.sentByUser ? email.sentByUser.name : null),
            status: email.status,
            htmlContent: email.htmlContent,
            textContent: email.textContent,
            priority: email.priority,
            messageId: email.messageId,
            templateId: email.templateId,
            templateVariables: email.templateVariables,
            deliveredAt: email.deliveredAt,
            openedAt: email.openedAt,
            clickedAt: email.clickedAt,
            bouncedAt: email.bouncedAt,
            bounceReason: email.bounceReason,
            failureReason: email.failureReason,
            retryCount: email.retryCount,
            createdAt: email.createdAt,
            updatedAt: email.updatedAt
          }))
        : [];
      
      console.log('Transformed emails:', transformedEmails); // Debug log
      setEmails(transformedEmails);
    } else {
      console.error('API call unsuccessful:', { isSuccess, data });
      setError('Failed to load emails');
    }
  } catch (err) {
    console.error('Error fetching emails:', err);
    setError(`An error occurred while fetching emails: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

  // Update selected email when URL emailId parameter changes
  useEffect(() => {
    const emailId = searchParams.get('emailId');
    if (emailId && emails.length > 0) {
      const email = emails.find(e => e.id.toString() === emailId);
      if (email) {
        setSelectedEmail(email);
      }
    } else {
      setSelectedEmail(null);
    }
  }, [searchParams, emails]);

  const handleEmailClick = (email) => {
    setSearchParams({ emailId: email.id });
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const handleNavigate = (direction) => {
    const currentIndex = emails.findIndex(e => e.id === selectedEmail.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex + 1 < emails.length ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : emails.length - 1;
    }
    
    const newEmail = emails[newIndex];
    setSearchParams({ emailId: newEmail.id });
  };

  useEffect(() => {
    setVisibleCount(20);
  }, [emails]);

  useEffect(() => {
    // if (isMobile) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (
        scrollTop + windowHeight >= fullHeight - 100 &&
        visibleCount < emails.length
      ) {
        setVisibleCount(prev => prev + 20);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [visibleCount, emails.length, isMobile]);

  // If an email is selected, show the detail view
  if (selectedEmail) {
    return (
      <EmailDetailView
        email={selectedEmail}
        onBack={handleBackToList}
        onNavigate={emails.length > 1 ? handleNavigate : null}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Alert 
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchEmails}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  // Email list view
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Emails
          </Typography>
          <Chip 
            label={emails.length}
            size="small"
            sx={{ 
              bgcolor: '#1976d2', 
              color: 'white',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          />
        </Box>
        
        {emails.length > 0 ? (
          isMobile ? (
            <EmailCardView 
              emails={visibleEmails} 
              formatDateTime={formatDateTime}
              onEmailClick={handleEmailClick}
            />
          ) : (
            <EmailTableView 
              emails={visibleEmails} 
              formatDateTime={formatDateTime}
              onEmailClick={handleEmailClick}
            />
          )
        ) : (
          <EmptyEmailState />
        )}

        {visibleCount < emails.length && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EmailsTab;